// The mode reels on the menu (ui-map-interface.md §16.5, R1535).
//
// A mode reel is the recorded video of one game mode that a PLAY tile
// plays: `client/public/reels/<name>.mp4` with its poster `<name>.webp`,
// which `scripts/build-reels.sh` records from the real engine. The hub's
// four PLAY tiles and the landing screen's two consoles show them.
//
// `reelHtml` writes the markup of one video. A `Reels` set plays the videos
// of one screen:
//
//   - A video plays only while it is on screen (an IntersectionObserver)
//     and the document is visible. Off screen or hidden, it pauses.
//   - Under `prefers-reduced-motion` a video never plays, so its poster
//     stays (§10.3).
//   - A browser that refuses autoplay rejects `play()`. The set catches the
//     rejection, and the poster stays.
//   - A screen that writes its markup again gets its old videos back:
//     `attachAll` puts each old video in the place of the new one of the
//     same reel, so a re-render does not start the reel again from 0.
//
// The video is muted as an attribute AND as a property, because iOS Safari
// plays inline with no gesture only a video whose `muted` property is set.

import { reducedMotion } from '../render/motion';

/** The four reels, one per mode of the PLAY tiles. */
export const REEL_NAMES = ['conquest', 'arcade', 'event', 'clanwar'] as const;
export type ReelName = typeof REEL_NAMES[number];

/** The address of a reel's video and of its poster. */
const reelVideo = (name: ReelName) => `/reels/${name}.mp4`;
export const reelPoster = (name: ReelName) => `/reels/${name}.webp`;

const isReel = (s: string | undefined): s is ReelName =>
  (REEL_NAMES as readonly string[]).includes(s ?? '');

/** The markup of one reel video. It is decoration, so a reader skips it. */
export function reelHtml(name: ReelName): string {
  return `<video class="reel" data-reel="${name}" muted loop playsinline preload="auto"`
    + ` disablepictureinpicture aria-hidden="true" tabindex="-1" poster="${reelPoster(name)}">`
    + `<source src="${reelVideo(name)}" type="video/mp4"></video>`;
}

/** The videos of one screen, and when each one plays. */
export class Reels {
  /** Each attached video, and whether the observer last saw it on screen. */
  private readonly shown = new Map<HTMLVideoElement, boolean>();
  private observer: IntersectionObserver | null = null;
  private listening = false;

  private readonly onVisibility = () => {
    for (const v of this.shown.keys()) this.update(v);
  };

  /**
   * Takes over every `video[data-reel]` under `root` and lets go of every
   * video it held that is not there. A new video whose reel an old,
   * detached video already plays is replaced by that old video.
   */
  attachAll(root: ParentNode): void {
    const old = new Map<string, HTMLVideoElement>();
    for (const v of this.shown.keys()) {
      if (!v.isConnected && v.dataset.reel) old.set(v.dataset.reel, v);
    }
    const kept = new Set<HTMLVideoElement>();
    for (const fresh of root.querySelectorAll<HTMLVideoElement>('video[data-reel]')) {
      const name = fresh.dataset.reel;
      if (!isReel(name)) continue;
      let v = fresh;
      const back = old.get(name);
      if (back !== undefined) {
        old.delete(name);
        fresh.replaceWith(back);
        v = back;
      }
      kept.add(v);
      this.attach(v);
    }
    for (const v of [...this.shown.keys()]) {
      if (!kept.has(v)) this.detach(v);
    }
  }

  /** Lets go of every video, and pauses each one. */
  detachAll(): void {
    for (const v of [...this.shown.keys()]) this.detach(v);
  }

  /** Lets go of every video and of the observer and the listener. */
  destroy(): void {
    this.detachAll();
    this.observer?.disconnect();
    this.observer = null;
  }

  private attach(v: HTMLVideoElement): void {
    if (this.shown.has(v)) {
      this.update(v);
      return;
    }
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    v.loop = true;
    // A browser without the observer treats every video as on screen.
    this.shown.set(v, true);
    this.watch()?.observe(v);
    if (!this.listening) {
      document.addEventListener('visibilitychange', this.onVisibility);
      this.listening = true;
    }
    this.update(v);
  }

  private detach(v: HTMLVideoElement): void {
    if (!this.shown.delete(v)) return;
    this.observer?.unobserve(v);
    v.pause();
    if (this.shown.size === 0 && this.listening) {
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.listening = false;
    }
  }

  /** The observer, built on first use; null where the browser has none. */
  private watch(): IntersectionObserver | null {
    if (this.observer !== null) return this.observer;
    if (typeof IntersectionObserver !== 'function') return null;
    try {
      this.observer = new IntersectionObserver((records) => {
        for (const r of records) {
          const v = r.target as HTMLVideoElement;
          if (!this.shown.has(v)) continue;
          this.shown.set(v, r.isIntersecting);
          this.update(v);
        }
      });
    } catch {
      this.observer = null;
    }
    return this.observer;
  }

  /** Plays or pauses one video by the three rules of the header. */
  private update(v: HTMLVideoElement): void {
    const play = this.shown.get(v) === true && !document.hidden && !reducedMotion();
    if (!play) {
      if (!v.paused) v.pause();
      return;
    }
    if (!v.paused) return;
    try {
      const p = v.play() as Promise<void> | undefined;
      // Autoplay refused, or the file is missing: the poster stays.
      p?.catch(() => { /* the poster stays */ });
    } catch {
      // An old browser throws in place of a rejected promise.
    }
  }
}
